import { ChevronRight, UploadCloud } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCreditMutations } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import type {
    CreditImportValidationResponse,
    SowCreditPoolSummary,
    SowUsageCreditImportValidationResponse,
} from '../../../services/api';
import { Button } from '../../ui/button';
import { overlineClassName } from '../../ui/typography';
import {
    formatCredits,
    getManualLedgerCompatiblePoolTypes,
    getPoolLabel,
    MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS,
} from './adminCreditAdapters';
import { Actions, DialogFrame, FormErrorAlert } from './creditDialogPrimitives';
import { getMutationErrorMessage } from './creditDialogHelpers';
import {
    downloadSowUsageCsvTemplate,
    SOW_USAGE_CSV_OPTIONAL_HEADERS,
    SOW_USAGE_CSV_REQUIRED_HEADERS,
    validateSowUsageCsvFile,
} from './sowUsageCsvImportClient';

interface SowUsageCsvImportDialogProps {
    open: boolean;
    onClose: () => void;
    sowId: string;
    sowName: string;
    pools: SowCreditPoolSummary[];
    teams: Array<{ teamId: number; label: string }>;
    onApplied?: (appliedRowCount: number) => void;
}

type ValidationSummary = SowUsageCreditImportValidationResponse['summary'];
type ValidationError = ValidationSummary['errors'][number];

function formatAmount(value: string | number): string {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? formatCredits(parsed) : String(value);
}

function getErrorMessage(error: unknown, fallback: string): string {
    return getMutationErrorMessage(error, fallback);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidationError(value: unknown): value is ValidationError {
    if (!isRecord(value)) {
        return false;
    }
    const rowNumber = value.rowNumber;
    const field = value.field;
    return (
        (typeof rowNumber === 'number' || rowNumber === null) &&
        (typeof field === 'string' || field === null) &&
        typeof value.code === 'string' &&
        typeof value.message === 'string' &&
        (value.rejectedValue === undefined || typeof value.rejectedValue === 'string')
    );
}

function isValidationSummary(value: unknown): value is ValidationSummary {
    if (!isRecord(value)) {
        return false;
    }
    return (
        typeof value.rowCount === 'number' &&
        typeof value.invalidRowCount === 'number' &&
        isRecord(value.amountsByPoolType) &&
        Array.isArray(value.errors) &&
        value.errors.every(isValidationError)
    );
}

function failedUploadValidationResponse(
    response: CreditImportValidationResponse['batch'],
): SowUsageCreditImportValidationResponse | null {
    if (response.status !== 'failed' || !isValidationSummary(response.validationSummary)) {
        return null;
    }
    return {
        batch: response,
        summary: response.validationSummary,
        rows: [],
    };
}

function SummaryCard({ label, value }: { label: string; value: number }) {
    return (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
            <p className={overlineClassName}>{label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-neutral-950">{value}</p>
        </div>
    );
}

function errorKey(error: ValidationError, index: number): string {
    return `${error.rowNumber ?? 'file'}-${error.field ?? 'file'}-${error.code}-${index}`;
}

export function SowUsageCsvImportDialog({
    open,
    onClose,
    sowId,
    sowName,
    pools,
    teams,
    onApplied,
}: SowUsageCsvImportDialogProps) {
    const { createCreditImport, validateCreditImport, applyCreditImport } = useCreditMutations();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFilename, setSelectedFilename] = useState('');
    const [clientError, setClientError] = useState<string | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const [validation, setValidation] = useState<SowUsageCreditImportValidationResponse | null>(
        null,
    );
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setSelectedFilename('');
        setClientError(null);
        setServerError(null);
        setValidation(null);
        setDragActive(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [open]);

    const currentPoolTypes = useMemo(
        () => new Set<string>(pools.map((pool) => pool.poolType)),
        [pools],
    );
    const compatibleArtifactRows = MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS.map((artifact) => ({
        ...artifact,
        poolLabels: getManualLedgerCompatiblePoolTypes(artifact.value)
            .filter((poolType) => currentPoolTypes.has(poolType))
            .map(getPoolLabel),
    })).filter((artifact) => artifact.poolLabels.length > 0);

    const importControlsDisabled =
        createCreditImport.isPending ||
        validateCreditImport.isPending ||
        applyCreditImport.isPending;
    const applyDisabled =
        validation?.batch.status !== 'validated' ||
        validation.summary.invalidRowCount > 0 ||
        validation.summary.errors.length > 0 ||
        applyCreditImport.isPending;
    const uploadPending = createCreditImport.isPending || validateCreditImport.isPending;

    const handleFile = async (file: File) => {
        if (importControlsDisabled) {
            return;
        }
        setSelectedFilename(file.name);
        setClientError(null);
        setServerError(null);
        setValidation(null);

        try {
            const validationMessage = validateSowUsageCsvFile(file);
            if (validationMessage) {
                setClientError(validationMessage);
                return;
            }

            const upload = await createCreditImport.mutateAsync({
                sowId,
                importType: 'sow_usage',
                filename: file.name,
                csv: await file.text(),
            });
            const failedValidation = failedUploadValidationResponse(upload.batch);
            if (failedValidation) {
                setValidation(failedValidation);
                return;
            }
            const nextValidation = await validateCreditImport.mutateAsync(upload.batch.id);
            setValidation(nextValidation);
        } catch (error) {
            setServerError(
                getErrorMessage(error, 'Could not upload and validate this CSV. Try again.'),
            );
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleApply = async () => {
        if (!validation || applyDisabled) {
            return;
        }
        setServerError(null);
        try {
            const result = await applyCreditImport.mutateAsync(validation.batch.id);
            onApplied?.(result.appliedRowCount);
            onClose();
        } catch (error) {
            setServerError(getErrorMessage(error, 'Could not apply this CSV import. Try again.'));
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragActive(false);
        if (importControlsDisabled) {
            return;
        }
        const [file] = Array.from(event.dataTransfer.files);
        if (file) {
            void handleFile(file);
        }
    };

    const openFilePicker = () => {
        if (importControlsDisabled) {
            return;
        }
        fileInputRef.current?.click();
    };

    const handleDropZoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (importControlsDisabled || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }
        event.preventDefault();
        openFilePicker();
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Import usage CSV"
            description="Upload usage rows into an existing SOW after validation."
            panelClassName="max-w-5xl"
        >
            <div className="space-y-5">
                <section className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
                    <p className={overlineClassName}>Destination SOW</p>
                    <p className="mt-1 text-base font-semibold text-neutral-950">{sowName}</p>
                    <p className="mt-2">
                        The import will validate every row against this SOW before anything is
                        applied.
                    </p>
                </section>

                {clientError ? <FormErrorAlert message={clientError} /> : null}
                {serverError ? <FormErrorAlert message={serverError} /> : null}

                <section
                    data-testid="sow-usage-csv-drop-zone"
                    role="button"
                    aria-disabled={importControlsDisabled}
                    tabIndex={importControlsDisabled ? -1 : 0}
                    onClick={openFilePicker}
                    onKeyDown={handleDropZoneKeyDown}
                    onDragEnter={(event) => {
                        event.preventDefault();
                        if (importControlsDisabled) {
                            return;
                        }
                        setDragActive(true);
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                        if (importControlsDisabled) {
                            return;
                        }
                        setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    className={cn(
                        'cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950',
                        importControlsDisabled
                            ? 'cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-400'
                            : dragActive
                              ? 'border-blue-500 bg-blue-50 text-blue-900'
                              : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400',
                    )}
                >
                    <UploadCloud className="mx-auto size-8 text-neutral-400" aria-hidden="true" />
                    <p className="mt-3 font-medium">
                        {dragActive ? 'Release to upload' : 'Drop a CSV file here'}
                    </p>
                    <p className="mt-1 text-sm text-neutral-500">
                        or choose a <span className="font-mono">.csv</span> file from your computer
                    </p>
                    {selectedFilename ? (
                        <p className="mt-3 text-sm font-medium text-neutral-950">
                            {selectedFilename}
                        </p>
                    ) : null}
                </section>
                <input
                    ref={fileInputRef}
                    type="file"
                    tabIndex={-1}
                    accept=".csv,text/csv"
                    aria-label="Choose SOW usage CSV"
                    className="sr-only"
                    disabled={importControlsDisabled}
                    onChange={(event) => {
                        const [file] = Array.from(event.currentTarget.files ?? []);
                        if (file) {
                            void handleFile(file);
                        }
                    }}
                />

                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={openFilePicker}
                        disabled={importControlsDisabled}
                    >
                        Choose CSV file
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={downloadSowUsageCsvTemplate}
                        disabled={importControlsDisabled}
                    >
                        Download template
                    </Button>
                </div>

                <details className="group rounded-lg border border-neutral-200 p-4">
                    <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-neutral-950 [&::-webkit-details-marker]:hidden">
                        <ChevronRight
                            className="size-4 shrink-0 text-neutral-500 transition-transform group-open:rotate-90 motion-reduce:transition-none"
                            aria-hidden
                        />
                        CSV field guide
                    </summary>
                    <div className="mt-4 space-y-4 text-sm text-neutral-700">
                        <p>Required headers: {SOW_USAGE_CSV_REQUIRED_HEADERS.join(', ')}.</p>
                        <p>Optional headers: {SOW_USAGE_CSV_OPTIONAL_HEADERS.join(', ')}.</p>
                        <p>
                            usageId must be unique within this file. It is not checked against
                            previous imports.
                        </p>
                        <p>
                            <code className="font-mono">userId</code> is the WEVO user attributed on
                            the ledger (not the admin uploading the file).{' '}
                            <code className="font-mono">amount</code> is required non-negative usage
                            (zero records artifact linkage without debiting credits);{' '}
                            <code className="font-mono">artifactType</code>,{' '}
                            <code className="font-mono">artifactId</code>, and{' '}
                            <code className="font-mono">occurredAt</code> are required artifact
                            linkage fields. <code className="font-mono">reason</code> is optional.
                            Use an ISO timestamp such as{' '}
                            <span className="font-mono">2026-09-01T14:30:00Z</span>.
                        </p>
                        <div>
                            <h3 className="font-medium text-neutral-950">Available teams</h3>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                {teams.map((team) => (
                                    <li key={team.teamId}>
                                        {team.teamId} - {team.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-medium text-neutral-950">Available pools</h3>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                {pools.map((pool) => (
                                    <li key={pool.id}>
                                        {getPoolLabel(pool.poolType)} (
                                        {formatAmount(pool.initialAmount)})
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-medium text-neutral-950">
                                Artifact and pool compatibility
                            </h3>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                {compatibleArtifactRows.map((artifact) => (
                                    <li key={artifact.value}>
                                        {artifact.label}: {artifact.poolLabels.join(', ')}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </details>

                <p className="text-sm text-neutral-600 empty:sr-only" role="status">
                    {uploadPending ? 'Uploading and validating CSV…' : ''}
                </p>

                {validation ? (
                    <section className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-3">
                            <SummaryCard label="Rows" value={validation.summary.rowCount} />
                            <SummaryCard
                                label="Invalid"
                                value={validation.summary.invalidRowCount}
                            />
                            <SummaryCard label="Errors" value={validation.summary.errors.length} />
                        </div>

                        <section className="rounded-lg border border-neutral-200 p-4">
                            <h3 className="text-sm font-semibold text-neutral-950">Pool totals</h3>
                            {Object.keys(validation.summary.amountsByPoolType).length === 0 ? (
                                <p className="mt-2 text-sm text-neutral-500">
                                    No valid pool totals found.
                                </p>
                            ) : (
                                <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                                    {Object.entries(validation.summary.amountsByPoolType).map(
                                        ([poolType, amount]) => (
                                            <div key={poolType}>
                                                <dt className="text-neutral-500">
                                                    {getPoolLabel(poolType)}
                                                </dt>
                                                <dd className="font-semibold text-neutral-950">
                                                    {formatAmount(amount)}
                                                </dd>
                                            </div>
                                        ),
                                    )}
                                </dl>
                            )}
                        </section>

                        {validation.summary.errors.length > 0 ? (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Validation errors
                                </h3>
                                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                                    <table
                                        className="min-w-full text-sm"
                                        aria-label="CSV validation errors"
                                    >
                                        <thead className="bg-neutral-50 text-left text-neutral-500">
                                            <tr>
                                                <th className="px-3 py-2 font-medium">Row</th>
                                                <th className="px-3 py-2 font-medium">Field</th>
                                                <th className="px-3 py-2 font-medium">
                                                    Rejected value
                                                </th>
                                                <th className="px-3 py-2 font-medium">Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validation.summary.errors.map((error, index) => (
                                                <tr
                                                    key={errorKey(error, index)}
                                                    className="border-t border-neutral-200"
                                                >
                                                    <td className="px-3 py-2 text-neutral-700">
                                                        {error.rowNumber ?? 'File'}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-700">
                                                        {error.field ?? 'File'}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-700">
                                                        {error.rejectedValue ?? '-'}
                                                    </td>
                                                    <td className="px-3 py-2 font-medium text-neutral-950">
                                                        {error.message}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        ) : (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                                CSV validated with no errors. Review the totals before applying.
                            </div>
                        )}
                    </section>
                ) : null}

                <Actions onCancel={onClose}>
                    <Button
                        type="button"
                        onClick={() => void handleApply()}
                        loading={applyCreditImport.isPending}
                        disabled={applyDisabled}
                    >
                        {applyCreditImport.isPending ? 'Applying…' : 'Apply import'}
                    </Button>
                </Actions>
            </div>
        </DialogFrame>
    );
}
